import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useCurrentUser } from '@/auth/context'
import { formatDate } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import type { Role } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field, TextField } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/** What an invitation may confer — mirrors `INVITABLE_ROLES` on the server. */
const INVITABLE: { value: Extract<Role, 'admin' | 'member'>; label: string; detail: string }[] = [
  {
    value: 'admin',
    label: 'Admin',
    detail: 'Everything except changing roles and deleting the organisation.',
  },
  { value: 'member', label: 'Member', detail: 'Read-only. Good for clients and stakeholders.' },
]

/**
 * The seven rows of S-D3's *What each role can do*, each mapped to the
 * permissions that actually gate it.
 *
 * **Rendered from `ROLE_CAPABILITIES`, never from a hand-typed grid of ticks.**
 * A literal table of ✓ and — would be a second, quieter copy of the permission
 * matrix, and the two would drift the first time a permission moved: the table
 * would go on promising something the buttons beside it already refuse. Every
 * cell below is the same `may()` lookup the rest of this screen uses, so the
 * matrix can only be wrong in the way the product is wrong.
 *
 * **Three columns where the mockup has four.** S-D3 draws Owner, Admin, Editor
 * and Viewer; the model has owner, admin and member. Editor and Viewer do not
 * exist (G25), and inventing the two columns here would be inventing a
 * permission model on a screen whose whole purpose is to report the real one —
 * so the table shows the roles that exist and the audit keeps handing the split
 * to the owner.
 *
 * *Invite & remove people* names two permissions because the mockup's row is
 * two capabilities; a tick needs both, which is the honest reading of an "and".
 */
const CAPABILITIES: { label: string; needs: readonly string[] }[] = [
  { label: 'View dashboards', needs: ['tenant:read'] },
  { label: 'Approve & publish content', needs: ['content:approve'] },
  { label: 'Change budget caps', needs: ['budget:manage'] },
  { label: 'Manage connections', needs: ['discovery:manage'] },
  { label: 'Invite & remove people', needs: ['members:invite', 'members:remove'] },
  { label: 'Billing & cancellation', needs: ['billing:manage'] },
  { label: 'Delete the organisation', needs: ['tenant:delete'] },
]

/** The roles the model actually has, in rank order. See `CAPABILITIES` on why. */
const MATRIX_ROLES: readonly Role[] = ['owner', 'admin', 'member']

/**
 * Team and roles.
 *
 * The role is chosen with its permissions visible rather than from a bare
 * dropdown (S-D3) — most wrong-permission incidents come from someone picking
 * "Admin" because it sounded standard.
 *
 * **An invitation cannot confer ownership.** That is enforced on the server
 * (`INVITABLE_ROLES`), and the reason is worth knowing: an emailed link that
 * could grant ownership would make the mailbox it was sent to equivalent to the
 * organisation.
 *
 * `email` is rendered with a fallback because it is **nullable** on every
 * response carrying a user: §4 permits an account created with a mobile number
 * and no address, and a screen printing `user.email` unconditionally would show
 * `null` for one.
 */
export function TeamPanel(): JSX.Element {
  const { tenant, role } = useActiveTenant()
  const { user } = useCurrentUser()
  const toast = useToast()
  const [inviting, setInviting] = useState(false)

  const canInvite = may(role, 'members:invite')
  const canRemove = may(role, 'members:remove')
  const canChangeRole = may(role, 'members:role')

  const members = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/tenants/{tenant_id}/members', {
          params: { path: { tenant_id: tenant.id } },
          signal,
        }),
      ),
    [tenant.id],
  )

  const invitations = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/tenants/{tenant_id}/invitations', {
          params: { path: { tenant_id: tenant.id } },
          signal,
        }),
      ),
    [tenant.id],
  )

  const changeRole = useMutation((input: { userId: string; role: Role }) =>
    call(() =>
      api.PATCH('/api/v1/tenants/{tenant_id}/members/{user_id}', {
        params: { path: { tenant_id: tenant.id, user_id: input.userId } },
        body: { role: input.role },
      }),
    ),
  )

  const removeMember = useMutation((userId: string) =>
    call(() =>
      api.DELETE('/api/v1/tenants/{tenant_id}/members/{user_id}', {
        params: { path: { tenant_id: tenant.id, user_id: userId } },
      }),
    ),
  )

  const revoke = useMutation((invitationId: string) =>
    call(() =>
      api.DELETE('/api/v1/tenants/{tenant_id}/invitations/{invitation_id}', {
        params: { path: { tenant_id: tenant.id, invitation_id: invitationId } },
      }),
    ),
  )

  function reloadAll(): void {
    members.refetch()
    invitations.refetch()
  }

  const seats = members.state.data?.seats ?? invitations.state.data?.seats ?? null

  return (
    <div className="ar-stack ar-stack--loose">
      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Team &amp; roles</h3>
          {seats ? (
            <span className="ar-badge ar-badge--neutral">
              {seats.used} {seats.limit === null ? 'in use' : `of ${String(seats.limit)} seats`}
            </span>
          ) : null}
          <span className="ar-card__spacer" />
          {canInvite ? (
            <Button
              tone="primary"
              size="sm"
              onClick={() => {
                setInviting(true)
              }}
            >
              Invite someone
            </Button>
          ) : null}
        </div>

        <InlineError
          failure={changeRole.state.failure ?? removeMember.state.failure ?? revoke.state.failure}
        />

        {members.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading the team" lines={3} />
          </div>
        ) : null}

        {members.state.status === 'error' ? (
          <ErrorState
            failure={members.state.failure}
            title="Could not load the team"
            onRetry={members.refetch}
          />
        ) : null}

        {members.state.status === 'ready' ? (
          <div className="ar-table-wrap">
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Last signed in</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {members.state.data.members.map((row) => {
                  const self = row.user_id === user.id
                  return (
                    <tr key={row.user_id}>
                      <td>
                        <strong>{row.email ?? row.mobile ?? 'Account with no address'}</strong>
                        {row.email && !row.email_verified ? (
                          <div
                            className="ar-subtle"
                            style={{ fontSize: 'var(--ar-text-caption-size)' }}
                          >
                            address not confirmed
                          </div>
                        ) : null}
                      </td>
                      <td>
                        {canChangeRole && !self ? (
                          <select
                            className="ar-select"
                            aria-label={`Role for ${row.email ?? row.user_id}`}
                            style={{
                              height: 'var(--ar-control-sm)',
                              fontSize: 'var(--ar-text-body-sm-size)',
                            }}
                            value={row.role}
                            disabled={changeRole.pending}
                            onChange={(event) => {
                              void (async () => {
                                const result = await changeRole.run({
                                  userId: row.user_id,
                                  role: event.target.value as Role,
                                })
                                if (result.ok) {
                                  reloadAll()
                                  toast.show({ kind: 'success', title: 'Role changed' })
                                }
                              })()
                            }}
                          >
                            <option value="owner">owner</option>
                            <option value="admin">admin</option>
                            <option value="member">member</option>
                          </select>
                        ) : (
                          <span className="ar-badge ar-badge--neutral">{row.role}</span>
                        )}
                      </td>
                      <td className="ar-muted">
                        {row.last_login_at ? formatDate(row.last_login_at) : '—'}
                      </td>
                      <td className="ar-num">
                        {canRemove && !self ? (
                          <Button
                            tone="quiet"
                            size="sm"
                            disabled={removeMember.pending}
                            onClick={() => {
                              void (async () => {
                                const result = await removeMember.run(row.user_id)
                                if (result.ok) {
                                  reloadAll()
                                  toast.show({ kind: 'success', title: 'Member removed' })
                                }
                              })()
                            }}
                          >
                            Remove
                          </Button>
                        ) : self ? (
                          <span
                            className="ar-subtle"
                            style={{ fontSize: 'var(--ar-text-caption-size)' }}
                          >
                            You
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Pending invitations</h3>
        </div>

        {invitations.state.status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading invitations" lines={2} />
          </div>
        ) : null}

        {invitations.state.status === 'error' ? (
          <ErrorState
            failure={invitations.state.failure}
            title="Could not load invitations"
            onRetry={invitations.refetch}
          />
        ) : null}

        {invitations.state.status === 'ready' && invitations.state.data.invitations.length === 0 ? (
          <Empty
            icon="✉"
            title="Nobody is waiting"
            body="An invitation holds a seat until it is accepted, expires after seven days, or is withdrawn."
          />
        ) : null}

        {invitations.state.status === 'ready' && invitations.state.data.invitations.length > 0 ? (
          <div className="ar-table-wrap">
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Role</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invitations.state.data.invitations.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.email}</strong>
                    </td>
                    <td>
                      <span className="ar-badge ar-badge--neutral">{row.role} · pending</span>
                    </td>
                    <td className="ar-muted">{formatDate(row.expires_at)}</td>
                    <td className="ar-num">
                      {canInvite ? (
                        <Button
                          tone="quiet"
                          size="sm"
                          disabled={revoke.pending}
                          onClick={() => {
                            void (async () => {
                              const result = await revoke.run(row.id)
                              if (result.ok) {
                                reloadAll()
                                toast.show({
                                  kind: 'success',
                                  title: 'Invitation withdrawn',
                                  body: 'Its seat is free again.',
                                })
                              }
                            })()
                          }}
                        >
                          Withdraw
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>What each role can do</h3>
        </div>
        <div className="ar-table-wrap">
          <table className="ar-table">
            <thead>
              <tr>
                <th scope="col">Capability</th>
                {MATRIX_ROLES.map((name) => (
                  <th key={name} scope="col">
                    {name}
                    {name === role ? ' · you' : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAPABILITIES.map((capability) => (
                <tr key={capability.label}>
                  <th scope="row" style={{ fontWeight: 400 }}>
                    {capability.label}
                  </th>
                  {MATRIX_ROLES.map((name) => {
                    const granted = capability.needs.every((permission) => may(name, permission))
                    return (
                      <td key={name}>
                        <span aria-hidden="true">{granted ? '✓' : '—'}</span>
                        <span className="ar-visually-hidden">{granted ? 'Yes' : 'No'}</span>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {inviting ? (
        <InviteDialog
          tenantId={tenant.id}
          onClose={() => {
            setInviting(false)
          }}
          onSent={(email, sent) => {
            setInviting(false)
            reloadAll()
            toast.show(
              sent
                ? { kind: 'success', title: 'Invitation sent', body: `${email} has seven days.` }
                : {
                    kind: 'warning',
                    title: 'Invitation created, email not sent',
                    body: `The invitation for ${email} exists. Withdraw and re-invite to try the email again.`,
                  },
            )
          }}
        />
      ) : null}
    </div>
  )
}

function InviteDialog({
  tenantId,
  onClose,
  onSent,
}: {
  tenantId: string
  onClose: () => void
  onSent: (email: string, sent: boolean) => void
}): JSX.Element {
  const [email, setEmail] = useState('')
  const [chosen, setChosen] = useState<'admin' | 'member'>('member')

  const invite = useMutation((body: { email: string; role: Role }) =>
    call(() =>
      api.POST('/api/v1/tenants/{tenant_id}/invitations', {
        params: { path: { tenant_id: tenantId } },
        body,
      }),
    ),
  )

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const address = email.trim().toLowerCase()
    const result = await invite.run({ email: address, role: chosen })
    if (result.ok) onSent(address, result.data.invitation_email_sent)
  }

  return (
    <Modal
      title="Invite someone"
      onClose={onClose}
      footer={
        <>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button tone="primary" type="submit" form="invite" busy={invite.pending}>
            Send invite
          </Button>
        </>
      }
    >
      <form
        id="invite"
        className="ar-stack"
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <InlineError failure={invite.state.failure} />
        <TextField
          label="Email address"
          type="email"
          required
          autoComplete="off"
          help="They get a link that expires in seven days. It only works for this address."
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
            invite.reset()
          }}
        />
        <Field label="Role">
          {() => (
            <div role="radiogroup" aria-label="Role">
              {INVITABLE.map((option) => (
                <label className="ar-choice ar-choice--radio" key={option.value}>
                  <input
                    type="radio"
                    name="role"
                    value={option.value}
                    checked={chosen === option.value}
                    onChange={() => {
                      setChosen(option.value)
                    }}
                  />
                  <span className="ar-choice__box" aria-hidden="true" />
                  <span className="ar-choice__text">
                    {option.label}
                    <span className="ar-choice__desc">{option.detail}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </Field>
      </form>
    </Modal>
  )
}
